import { makeAutoObservable, action } from "mobx";
import keycloak from "../core/keycloak";
import { SidebarState } from "./global.store";

interface ConversationSubscription {
  eventSource: EventSource;
  reconnectAttempts: number;
  reconnectTimeout: NodeJS.Timeout | null;
  isIntentionallyClosed: boolean;
  onMessageCallback: (data: any) => void;
  onErrorCallback?: (error: Error) => void;
}

interface AgentSubscription {
  eventSource: EventSource;
  reconnectAttempts: number;
  reconnectTimeout: NodeJS.Timeout | null;
  isIntentionallyClosed: boolean;
  onNewConversation?: (data: any) => void;
  onConversationUpdate?: (data: any) => void;
  onErrorCallback?: (error: Error) => void;
}

class ConversationsStore {
  // Map of conversation IDs to their active SSE subscriptions
  subscriptions: Map<string, ConversationSubscription> = new Map();

  // Track conversations with unread messages
  conversationsWithMessages: Set<string> = new Set();

  // Agent subscription for new conversations
  agentSubscription: AgentSubscription | null = null;

  // Reference to global store (injected after initialization)
  globalStore: any = null;

  // Track last fetch time to avoid duplicates
  lastFetchTime: string = new Date().toISOString();

  constructor() {
    makeAutoObservable(this, {
      setGlobalStore: action,
      subscribeToConversation: action,
      unsubscribeFromConversation: action,
      unsubscribeFromConversationById: action,
      unsubscribeFromAll: action,
      addConversationWithMessage: action,
      removeConversationWithMessage: action,
      clearUnreadMessages: action,
      subscribeToAgent: action,
      unsubscribeFromAgent: action,
      updateLastFetchTime: action,
    });
  }

  // Inject global store reference to handle sidebar expansion
  setGlobalStore = (globalStore: any) => {
    this.globalStore = globalStore;
  };

  /**
   * Add a conversation ID to the set with unread messages
   */
  addConversationWithMessage = action((conversationId: string) => {
    this.conversationsWithMessages.add(conversationId);
  });

  /**
   * Remove a conversation ID from the set with unread messages
   */
  removeConversationWithMessage = action((conversationId: string) => {
    this.conversationsWithMessages.delete(conversationId);
  });

  /**
   * Subscribe to a conversation SSE stream
   * Keeps the connection alive even when navigating away from the page
   */
  subscribeToConversation = (
    conversationId: string,
    userId: string,
    onMessage: (data: any) => void,
    onError?: (error: Error) => void
  ) => {
    // If already subscribed, just update the callback
    if (this.subscriptions.has(conversationId)) {
      console.log(`✅ SSE: Already subscribed to conversation ${conversationId}, updating callback`);
      this.updateSubscriptionCallback(conversationId, onMessage, onError);
      return;
    }

    console.log(`🔵 SSE: Starting subscription to conversation ${conversationId} for user ${userId}`);

    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let reconnectTimeout: NodeJS.Timeout | null = null;
    let isIntentionallyClosed = false;

    const connect = () => {
      console.log(`🔗 SSE: Attempting to connect to conversation ${conversationId} (attempt ${reconnectAttempts + 1})`);
      try {
        const token = keycloak.token;
        if (!token) {
          console.error('❌ SSE: No authentication token available');
          return;
        }

        const baseUrl = `${import.meta.env.VITE_API_BASE_URL}/chat/stream/${conversationId}`;
        const params = new URLSearchParams({
          userId: userId,
          token: token,
        });
        const sseUrl = `${baseUrl}?${params.toString()}`;

        console.log("🌐 SSE: Opening EventSource to", baseUrl, `[userId=${userId}]`);

        // Close any existing connection first
        const existing = this.subscriptions.get(conversationId);
        if (existing) {
          try {
            existing.eventSource.close();
          } catch (e) {
            /* ignore */
          }
        }

        const es = new EventSource(sseUrl);

        es.onopen = () => {
          console.log("✅ SSE: Connection OPENED for conversation", conversationId);
          reconnectAttempts = 0;
        };

        es.onmessage = (evt) => {
          try {
            const payload = JSON.parse(evt.data);
            console.log("📨 SSE: Received message from", conversationId, {
              conversationId,
              type: payload.type,
              hasData: !!payload.data,
            });

            switch (payload.type) {
              case "connected":
                console.log("✅ SSE: Connected successfully", payload);
                break;
              case "heartbeat":
                console.log("💓 SSE: Heartbeat from conversation", conversationId);
                break;
              case "typing":
                console.log("⌨️  SSE: Typing from conversation", conversationId);
                break;
              case "message":
                console.log("💬 SSE: NEW MESSAGE from conversation", conversationId);
                const messageData = payload.data || payload;

                // Only mark as unread if message is from customer or flow, not from agent (current user)
                if (messageData.senderType !== 'agent') {
                  console.log("🔴 SSE: Message is from external source, checking if should show notification");

                  // Check if this is the currently selected conversation
                  this.addConversationWithMessage(conversationId);

                  // Auto-expand sidebar when message arrives
                  if (this.globalStore) {
                    console.log("📍 SSE: Expanding sidebar due to new message");
                    if (this.globalStore.sidebarState === SidebarState.ICONS_ONLY) {
                      this.globalStore.setSidebarState(SidebarState.EXPANDED);
                    } else if (this.globalStore.sidebarState === SidebarState.COLLAPSED && !this.globalStore.isMobileView) {
                      this.globalStore.setSidebarState(SidebarState.EXPANDED);
                    }
                  }
                } else {
                  console.log("⚪ SSE: Message is from agent (current user), skipping notification");
                }

                // Call the stored callback
                const subscription = this.subscriptions.get(conversationId);
                if (subscription && subscription.onMessageCallback) {
                  subscription.onMessageCallback(messageData);
                } else {
                  console.warn("⚠️ SSE: No callback found for conversation", conversationId);
                }
                break;
              default:
                console.log("❓ SSE: Unknown message type:", payload.type);
            }
          } catch (error) {
            console.error("❌ SSE: Error parsing message from", conversationId, error, evt.data);
            const subscription = this.subscriptions.get(conversationId);
            if (subscription && subscription.onErrorCallback) {
              subscription.onErrorCallback(error as Error);
            }
          }
        };

        es.onerror = (err) => {
          console.error(
            "❌ SSE: Connection error for conversation",
            conversationId,
            "readyState:",
            es.readyState,
            err
          );

          if (es.readyState === EventSource.CLOSED) {
            if (!isIntentionallyClosed && reconnectAttempts < maxReconnectAttempts) {
              reconnectAttempts++;
              const delay = Math.min(
                1000 * Math.pow(2, reconnectAttempts - 1),
                30000
              );
              console.log(
                `🔄 SSE: Will reconnect in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`
              );

              reconnectTimeout = setTimeout(() => {
                connect();
              }, delay);
            } else if (reconnectAttempts >= maxReconnectAttempts) {
              console.error(
                "❌ SSE: Max reconnection attempts reached for conversation",
                conversationId
              );
            }
          }

          try {
            es.close();
          } catch (e) {
            /* ignore */
          }

          if (reconnectAttempts >= maxReconnectAttempts) {
            // Remove from subscriptions if max retries exceeded
            this.subscriptions.delete(conversationId);
            this.removeConversationWithMessage(conversationId);
          }
        };

        // Store subscription with callback
        this.subscriptions.set(conversationId, {
          eventSource: es,
          reconnectAttempts,
          reconnectTimeout,
          isIntentionallyClosed,
          onMessageCallback: onMessage,
          onErrorCallback: onError
        });

        console.log(`📊 SSE: Active subscriptions count: ${this.subscriptions.size}`);
      } catch (error) {
        console.error(
          "❌ SSE: Failed to establish connection for conversation",
          conversationId,
          error
        );
      }
    };

    connect();
  };

  /**
   * Unsubscribe from a conversation by its ID
   * This is used when a conversation is closed or transferred
   */
  unsubscribeFromConversationById = action((conversationId: string) => {
    console.log(`🛑 SSE: Unsubscribing from conversation ${conversationId} by ID`);

    const subscription = this.subscriptions.get(conversationId);
    if (!subscription) {
      console.log(`⚠️  SSE: Not subscribed to conversation ${conversationId}`);
      return;
    }

    subscription.isIntentionallyClosed = true;

    if (subscription.reconnectTimeout) {
      clearTimeout(subscription.reconnectTimeout);
      subscription.reconnectTimeout = null;
    }

    try {
      subscription.eventSource.close();
    } catch (e) {
      console.warn("SSE: Error closing connection", e);
    }

    this.subscriptions.delete(conversationId);
    this.removeConversationWithMessage(conversationId);
    console.log(`📊 SSE: Active subscriptions count: ${this.subscriptions.size}`);
  });

  /**
   * Unsubscribe from a conversation SSE stream (by conversation ID and userId)
   */
  unsubscribeFromConversation = (
    conversationId: string,
    userId: string,
    sessionId?: string,
  ) => {
    const listenerId = `${userId}:${sessionId ?? 'default'}`;
    const subscription = this.subscriptions.get(conversationId);

    if (!subscription) {
      console.log(`⚠️  SSE: Not subscribed to conversation ${conversationId}`);
      return;
    }

    console.log(`🛑 SSE: Unsubscribing from conversation ${conversationId}`);
    subscription.isIntentionallyClosed = true;

    if (subscription.reconnectTimeout) {
      clearTimeout(subscription.reconnectTimeout);
      subscription.reconnectTimeout = null;
    }

    try {
      subscription.eventSource.close();
    } catch (e) {
      console.warn("SSE: Error closing connection", e);
    }

    this.subscriptions.delete(conversationId);
    this.removeConversationWithMessage(conversationId);
    console.log(`📊 SSE: Active subscriptions count: ${this.subscriptions.size}`);
  };

  /**
   * Subscribe to agent conversation events
   */
  subscribeToAgent = action((
    agentId: string,
    onNewConversation: (data: any) => void,
    onConversationUpdate: (data: any) => void,
    onError?: (error: Error) => void
  ) => {
    console.log(`🔵 Agent SSE: Starting subscription for agent ${agentId}`);

    if (this.agentSubscription) {
      console.log(`⚠️ Agent SSE: Already subscribed, updating callbacks`);
      this.updateAgentSubscriptionCallbacks(onNewConversation, onConversationUpdate, onError);
      return;
    }

    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    let reconnectTimeout: NodeJS.Timeout | null = null;
    let isIntentionallyClosed = false;

    const connect = () => {
      console.log(`🔗 Agent SSE: Attempting to connect for agent ${agentId} (attempt ${reconnectAttempts + 1})`);
      try {
        const token = keycloak.token;
        if (!token) {
          console.error('❌ Agent SSE: No authentication token available');
          return;
        }

        const baseUrl = `${import.meta.env.VITE_API_BASE_URL}/chat/stream/agent/${agentId}`;
        const params = new URLSearchParams({
          token: token,
          lastFetchTime: this.lastFetchTime,
        });
        const sseUrl = `${baseUrl}?${params.toString()}`;

        console.log("🌐 Agent SSE: Opening EventSource to", baseUrl);

        // Close any existing connection first
        if (this.agentSubscription) {
          try {
            this.agentSubscription.eventSource.close();
          } catch (e) {
            /* ignore */
          }
        }

        const es = new EventSource(sseUrl);

        es.onopen = () => {
          console.log("✅ Agent SSE: Connection OPENED for agent", agentId);
          reconnectAttempts = 0;
        };

        es.onmessage = (evt) => {
          try {
            const payload = JSON.parse(evt.data);
            console.log("📨 Agent SSE: Received event", {
              type: payload.type,
              eventType: payload.eventType,
              conversationId: payload.data?.id,
            });

            switch (payload.type) {
              case "connected":
                console.log("✅ Agent SSE: Connected successfully", payload);
                break;
              case "heartbeat":
                console.log("💓 Agent SSE: Heartbeat");
                break;
              case "conversation_event":
                const eventData = payload.data;
                const eventType = payload.eventType;

                switch (eventType) {
                  case "conversation_created":
                  case "conversation_assigned":
                    console.log("🆕 Agent SSE: NEW/ASSIGNED CONVERSATION", eventData);
                    if (onNewConversation) {
                      onNewConversation(eventData);
                    }
                    break;
                  case "conversation_updated":
                  case "conversation_closed":
                  case "conversation_expired":
                    console.log("🔄 Agent SSE: CONVERSATION UPDATE", eventData);
                    if (onConversationUpdate) {
                      onConversationUpdate(eventData);
                    }
                    break;
                  default:
                    console.log("❓ Agent SSE: Unknown conversation event type:", eventType);
                }
                break;
              default:
                console.log("❓ Agent SSE: Unknown message type:", payload.type);
            }
          } catch (error) {
            console.error("❌ Agent SSE: Error parsing message", error, evt.data);
            if (onError) {
              onError(error as Error);
            }
          }
        };

        es.onerror = (err) => {
          console.error(
            "❌ Agent SSE: Connection error",
            "readyState:",
            es.readyState,
            err
          );

          if (es.readyState === EventSource.CLOSED) {
            if (!isIntentionallyClosed && reconnectAttempts < maxReconnectAttempts) {
              reconnectAttempts++;
              const delay = Math.min(
                1000 * Math.pow(2, reconnectAttempts - 1),
                30000
              );
              console.log(
                `🔄 Agent SSE: Will reconnect in ${delay}ms (attempt ${reconnectAttempts}/${maxReconnectAttempts})`
              );

              reconnectTimeout = setTimeout(() => {
                connect();
              }, delay);
            } else if (reconnectAttempts >= maxReconnectAttempts) {
              console.error("❌ Agent SSE: Max reconnection attempts reached");
              this.agentSubscription = null;
            }
          }

          try {
            es.close();
          } catch (e) {
            /* ignore */
          }
        };

        // Store agent subscription
        this.agentSubscription = {
          eventSource: es,
          reconnectAttempts,
          reconnectTimeout,
          isIntentionallyClosed,
          onNewConversation,
          onConversationUpdate,
          onErrorCallback: onError
        };

        console.log(`📊 Agent SSE: Agent subscription started for agent ${agentId}`);
      } catch (error) {
        console.error("❌ Agent SSE: Failed to establish connection", error);
      }
    };

    connect();
  });

  /**
   * Update agent subscription callbacks
   */
  updateAgentSubscriptionCallbacks = (
    onNewConversation: (data: any) => void,
    onConversationUpdate: (data: any) => void,
    onError?: (error: Error) => void
  ) => {
    if (this.agentSubscription) {
      this.agentSubscription.onNewConversation = onNewConversation;
      this.agentSubscription.onConversationUpdate = onConversationUpdate;
      if (onError) {
        this.agentSubscription.onErrorCallback = onError;
      }
    }
  };

  /**
   * Unsubscribe from agent events
   */
  unsubscribeFromAgent = action(() => {
    if (!this.agentSubscription) {
      console.log(`⚠️ Agent SSE: No agent subscription active`);
      return;
    }

    console.log(`🛑 Agent SSE: Unsubscribing from agent events`);
    this.agentSubscription.isIntentionallyClosed = true;

    if (this.agentSubscription.reconnectTimeout) {
      clearTimeout(this.agentSubscription.reconnectTimeout);
      this.agentSubscription.reconnectTimeout = null;
    }

    try {
      this.agentSubscription.eventSource.close();
    } catch (e) {
      console.warn("Agent SSE: Error closing connection", e);
    }

    this.agentSubscription = null;
    console.log(`📊 Agent SSE: Agent subscription stopped`);
  });

  /**
   * Unsubscribe from all conversations (e.g., on logout or tab switch)
   */
  unsubscribeFromAll = action(() => {
    console.log("🛑 SSE: Unsubscribing from ALL conversations");
    const conversationIds = Array.from(this.subscriptions.keys());
    console.log(`Closing ${conversationIds.length} subscriptions:`, conversationIds);
    conversationIds.forEach((conversationId) => {
      this.unsubscribeFromConversationById(conversationId);
    });
  });

  /**
   * Check if conversation has unread messages
   */
  hasUnreadMessages = (conversationId: string): boolean => {
    return this.conversationsWithMessages.has(conversationId);
  };

  /**
   * Clear unread messages for a conversation
   */
  clearUnreadMessages = action((conversationId: string) => {
    this.conversationsWithMessages.delete(conversationId);
  });

  /**
   * Get all conversations with unread messages
   */
  getConversationsWithMessages = (): string[] => {
    return Array.from(this.conversationsWithMessages);
  };

  /**
   * Update subscription callback
   */
  updateSubscriptionCallback = (
    conversationId: string,
    newCallback: (data: any) => void,
    newErrorCallback?: (error: Error) => void
  ) => {
    const subscription = this.subscriptions.get(conversationId);
    if (!subscription) {
      console.log(`⚠️ SSE: Not subscribed to conversation ${conversationId}, cannot update callback`);
      return;
    }

    console.log(`🔄 SSE: Updating callback for conversation ${conversationId}`);
    subscription.onMessageCallback = newCallback;
    if (newErrorCallback) {
      subscription.onErrorCallback = newErrorCallback;
    }
  };

  /**
   * Check and clear notification for a conversation if user is viewing it
   */
  checkAndClearNotification = (conversationId: string) => {
    if (this.conversationsWithMessages.has(conversationId)) {
      console.log(`🔔 Clearing notification for conversation ${conversationId}`);
      this.conversationsWithMessages.delete(conversationId);
    }
  };

  /**
   * Clear all notifications
   */
  clearAllNotifications = action(() => {
    console.log('🗑️ Clearing ALL notifications');
    this.conversationsWithMessages.clear();
  });

  /**
   * Update last fetch time to avoid duplicate events
   */
  updateLastFetchTime = action(() => {
    this.lastFetchTime = new Date().toISOString();
  });
}

export default ConversationsStore;